import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import DataTable from '@/components/DataTable';
import {
  Modal,
  Input,
  InputNumber,
  Pagination,
  Card,
  Tooltip,
  Space,
  Button,
  Tag,
  Typography,
  Upload,
  Divider,
  Form,
  Row,
  Col,
  Empty,
  Tabs,
  Alert,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  bp_get_qc_sample_queue,
  bp_get_sample_detailss,
  bp_get_sampling_details_by_sample,
  bp_save_sample_actual_data,
  store_sample_detail_status,
} from '@/utils/api';
import ReusableSearchInput from '@/components/ReusableSearch';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  CheckCheck,
  RotateCw,
  UploadCloud,
  FileText,
  FlaskConical,
  Eye,
  Calendar,
  MessageCircle,
  XCircle,
} from 'lucide-react';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import Icons from '@/components/third-party/Icons';
import PageHeader from '@/components/PageHeader';
import {
  ClipboardList as KpiAllIcon, Hourglass as KpiWaitIcon, FlaskConical as KpiQcIcon, CheckCircle2 as KpiOkIcon, XCircle as KpiFailIcon,
  Ban as StBan, CircleCheck as StPass, Hourglass as StWait, Info as StInfo, Lock as StLock, MapPin as StPlace, OctagonX as StFail,
  PenLine as StEnter, RotateCw as StRedo, Send as StSent, TestTube as StDrawn, TriangleAlert as StWarn, Unlink as StUnlinked,
} from 'lucide-react';
import { AgeLabel, CountChip, IconLabel, SummaryTiles, TONE } from '@/components/queue/QueueKit';
import { formatDate, formatDateTime } from '@/utils/datetime';
import { useMbmrAlias } from '@/hooks/useAppSetting';

const { Title, Text, Link } = Typography;
const { TextArea } = Input;

// ONE status vocabulary for every tag on this page: legacy Sample_Details statuses, kind-based
// SampleRecord statuses and LIMS pull statuses. `bucket` feeds the summary counters.
export const SAMPLE_STATUS = {
  // legacy Sample_Details
  Pending: { label: 'Pending', color: 'warning', bucket: 'pending' },
  'Sent to QC': { label: 'Sent to QC', color: 'processing', bucket: 'pending' },
  'Under test': { label: 'Under test', color: 'processing', bucket: 'pending' },
  Resampling: { label: 'Resampling', color: 'processing', bucket: 'pending' },
  Approved: { label: 'Passed', color: 'success', bucket: 'passed' },
  Completed: { label: 'Passed', color: 'success', bucket: 'passed' },
  Rejected: { label: 'Failed', color: 'error', bucket: 'failed' },
  // kind-based SampleRecord
  DRAWN: { label: 'Drawn', color: 'default', bucket: 'pending' },
  SENT: { label: 'Sent to lab', color: 'processing', bucket: 'pending' },
  PENDING: { label: 'Pending', color: 'warning', bucket: 'pending' },
  AWAITING_RESULT: { label: 'Awaiting result', color: 'processing', bucket: 'pending' },
  PASS: { label: 'Pass', color: 'success', bucket: 'passed' },
  FAIL: { label: 'Fail / OOS', color: 'error', bucket: 'failed' },
  INVALIDATED: { label: 'Invalidated', color: 'default', bucket: 'other' },
  INVALID: { label: 'Invalidated', color: 'default', bucket: 'other' },
  // LIMS pull status (LimsSampleLink)
  NOT_LINKED: { label: 'Not in LIMS', color: 'warning', bucket: 'pending' },
  UNREGISTERED: { label: 'Not registered', color: 'warning', bucket: 'pending' },
  PARTIAL: { label: 'Tests running', color: 'processing', bucket: 'pending' },
  COMPLETE: { label: 'Results recorded', color: 'success', bucket: 'passed' },
  ERROR: { label: 'Pull error', color: 'error', bucket: 'failed' },
  IN_PLACE: { label: 'In-place', color: 'default', bucket: 'other' },
};
const statusMeta = (v) => SAMPLE_STATUS[v] || { label: v || '—', color: 'default', bucket: 'pending' };
// The status as icon + short word (QueueKit): the icon says the state at a glance, the colour its meaning.
const STATUS_ICON = {
  Pending: [StWait, TONE.amber], PENDING: [StWait, TONE.amber], 'Sent to QC': [StSent, TONE.blue], SENT: [StSent, TONE.blue],
  'Under test': [KpiQcIcon, TONE.blue], AWAITING_RESULT: [KpiQcIcon, TONE.blue], PARTIAL: [KpiQcIcon, TONE.blue],
  Resampling: [StRedo, TONE.purple], DRAWN: [StDrawn, TONE.slate],
  NOT_LINKED: [StUnlinked, TONE.amber], UNREGISTERED: [StUnlinked, TONE.amber], ERROR: [StWarn, TONE.red],
  INVALIDATED: [StBan, TONE.slate], INVALID: [StBan, TONE.slate], IN_PLACE: [StPlace, TONE.slate],
};
const StatusTag = ({ value }) => {
  const m = statusMeta(value);
  const [Icon, color] = STATUS_ICON[value] || (m.bucket === 'passed' ? [StPass, TONE.green] : m.bucket === 'failed' ? [StFail, TONE.red] : [StWait, TONE.slate]);
  return <IconLabel Icon={Icon} color={color} label={m.label} />;
};
// One line, cut with "…", the full text on hover (the shared table wraps cell text otherwise).
const Clip = ({ text, max, small, secondary }) => (
  <Tooltip title={text}>
    <span style={{ display: 'block', maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      fontSize: small ? 12 : undefined, color: secondary ? 'var(--ant-color-text-secondary, #6b7280)' : undefined }}>{text}</span>
  </Tooltip>
);
const KIND_COLOR = { IPC_LAB: 'blue', IPC_INLINE: 'cyan', RELEASE_BULK: 'purple', RELEASE_FINISHED: 'magenta', RETENTION: 'default', STABILITY: 'geekblue', EM: 'green', CLEANING_VERIFICATION: 'lime', RETEST: 'orange' };
const bpAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });

// Same min/max spec comparison used by the saved-value Tag, but reusable for the live-typed value.
// Returns { hasSignal, inSpec } — hasSignal is false when there is no value or no bound to check against.
const specSignal = (param, raw) => {
  const hasVal = raw !== '' && raw !== null && raw !== undefined;
  const hasBounds = param?.sampling_min_value != null || param?.sampling_max_value != null;
  if (!hasVal || !hasBounds) return { hasSignal: false, inSpec: true };
  const inSpec =
    Number(raw) >= Number(param.sampling_min_value ?? -Infinity) &&
    Number(raw) <= Number(param.sampling_max_value ?? Infinity);
  return { hasSignal: true, inSpec };
};

function SampleTesting() {
  const [sample_data, set_sample_data] = useState([]);
  const [total, set_total] = useState(0);
  const [page_number, set_page_number] = useState(1);
  const [searchvalue, setSearchvalue] = useState('');
  // ?batch_number= / ?batch= from the URL (cockpit / insights deep links) and ?sample= to highlight a row
  const [searchParams, setSearchParams] = useSearchParams();
  const [batchFilter, setBatchFilter] = useState(searchParams.get('batch_number') || searchParams.get('batch') || '');
  const highlightSample = searchParams.get('sample') || '';
  const [tab, setTab] = useState(searchParams.get('tab') || (searchParams.get('sample') ? 'kind' : 'queue'));
  // "All samples" tab (bp_get_sample_detailss)
  const [all_data, set_all_data] = useState([]);
  const [all_total, set_all_total] = useState(0);
  const [server_summary, set_server_summary] = useState(null);   // KPI strip counted over the whole filtered set, not the page
  const [all_page, set_all_page] = useState(1);
  // "Samples by kind" tab — SampleRecord, not Sample_Details (see the note rendered on the tab).
  // `kind_summary` is the per-kind breakdown (always available, plant-wide when no batch is typed);
  // `kind_rows` is the per-batch sample list, which only exists once a batch number is given.
  const [kind_rows, set_kind_rows] = useState([]);
  const [kind_summary, set_kind_summary] = useState(null);
  const [kind_error, set_kind_error] = useState(null);
  const [kind_loading, set_kind_loading] = useState(false);

  // QC entry form modal
  const [qc_form_open, set_qc_form_open] = useState(false);
  const [active_record, set_active_record] = useState(null);
  const [paramter_details, set_paramter_details] = useState([]);
  const [documents, set_documents] = useState([]);
  const [loading_form, set_loading_form] = useState(false);

  // Reject state inside QC modal
  const [reject_mode, set_reject_mode] = useState(false);
  const [reject_comment, set_reject_comment] = useState('');
  const [rejecting, set_rejecting] = useState(false);
  const [save_error, set_save_error] = useState(null); // inline banner for a failed save (not only a toast)

  // View results modal (Completed / Rejected)
  const [view_open, set_view_open] = useState(false);
  const [view_params, set_view_params] = useState([]);
  const [view_docs, set_view_docs] = useState([]);
  const [view_record, set_view_record] = useState(null);
  const [view_empty_reason, set_view_empty_reason] = useState(null);
  const [loading_view, set_loading_view] = useState(false);

  // E-signature
  const [showESignatureModal, setShowESignatureModal] = useState(false);
  const [pendingApprovalSample, setPendingApprovalSample] = useState(null);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const isESign = accessControlUtils('ESign')?.create_access || false;
  const mbmrAlias = useMbmrAlias();

  // ── API calls ──────────────────────────────────────────────────────────────

  const get_data = async () => {
    try {
      await bp_get_qc_sample_queue(
        { page_number, ...(searchvalue && { search: searchvalue }), ...(batchFilter && { batch_number: batchFilter }) },
        (res) => {
          set_sample_data(res?.data?.data || []);
          set_total(res?.data?.obj_count || 0);
          // get_qc_sample_queue emits NO `summary` key. The old `if (res.data.summary)` therefore
          // never wrote here and left whatever the all-samples fetch had written — so the strip read
          // 157/1/1/156/0 over a five-row queue and "Failed / OOS" was stuck at 0 while the same
          // batch carried an OOS. Each tab now keeps its own counts (see summaryData).
        }
      );
    } catch (error) {
      console.error('Error fetching QC queue:', error);
    }
  };

  const get_all_samples = useCallback(async () => {
    try {
      await bp_get_sample_detailss(
        { page_number: all_page, ...(searchvalue && { search: searchvalue }), ...(batchFilter && { batch_number: batchFilter }) },
        (res) => {
          set_all_data(res?.data?.data || []);
          set_all_total(res?.data?.obj_count || 0);
          set_server_summary(res?.data?.summary || null);
        }
      );
    } catch (error) {
      console.error('Error fetching samples:', error);
    }
  }, [all_page, searchvalue, batchFilter]);

  // GET /bp/samples/by-kind/ — SampleRecord, gated on "Sample Testing" (the module this page is
  // menued under). The previous source, /bp/integrations/lims/samples/, needed a batch number AND
  // read on "System Integration", which a QC analyst does not hold: the tab 403'd and rendered an
  // empty table with a toast, so the kind breakdown was never visible to the people who need it.
  const get_kind_samples = useCallback(async () => {
    set_kind_loading(true);
    try {
      const res = await Axios.get('/bp/samples/by-kind/', { ...bpAuth(), params: { ...(batchFilter && { batch_number: batchFilter }) } });
      const d = res?.data?.data || {};
      set_kind_summary(d);
      set_kind_rows(d.samples || []);
      set_kind_error(res?.data?.status === 'success' ? null : (res?.data?.message || 'Could not load the sample kinds'));
    } catch (error) {
      const msg = error?.response?.data?.message || 'Could not load the sample kinds';
      set_kind_error(msg);
      set_kind_summary(null);
      set_kind_rows([]);
    } finally {
      set_kind_loading(false);
    }
  }, [batchFilter]);

  const open_qc_form = async (record) => {
    set_active_record(record);
    set_paramter_details([]);
    set_documents([]);
    set_reject_mode(false);
    set_reject_comment('');
    set_save_error(null);
    set_qc_form_open(true);
    set_loading_form(true);
    try {
      await bp_get_sampling_details_by_sample(
        (res) => {
          set_paramter_details(res?.data?.data || []);
          set_documents(res?.data?.documents || []);
        },
        { sample_detail_id: record?.sample_detail_id }
      );
    } catch (error) {
      console.error('Error fetching sample details:', error);
    } finally {
      set_loading_form(false);
    }
  };

  const open_view_modal = async (record) => {
    set_view_record(record);
    set_view_params([]);
    set_view_docs([]);
    set_view_empty_reason(null);
    set_view_open(true);
    set_loading_view(true);
    try {
      await bp_get_sampling_details_by_sample(
        (res) => {
          set_view_params(res?.data?.data || []);
          set_view_docs(res?.data?.documents || []);
          // The detail endpoint returns the full sample (result, observation,
          // spec, who tested it) — merge it over the table row so the drawer
          // renders even when the row came from a leaner list payload.
          if (res?.data?.sample) set_view_record((prev) => ({ ...(prev || {}), ...res.data.sample }));
          set_view_empty_reason(res?.data?.empty_reason || null);
        },
        { sample_detail_id: record?.sample_detail_id }
      );
    } catch (error) {
      console.error('Error fetching sample details:', error);
    } finally {
      set_loading_view(false);
    }
  };

  const call_status_api = async ({ record, sample_status, description, onSuccess }) => {
    try {
      await store_sample_detail_status(
        {
          sample_detail_id: record?.sample_detail_id,
          sample_number: record?.sample_number,
          batch_number: record?.batch_number,
          operation_id: record?.operation_id,
          batch_id: record?.batch_id,
          sample_status,
          ...(description ? { description } : {}),
        },
        (res) => {
          if (res?.status === 'success') {
            toast.success(res?.message || 'Status updated');
            onSuccess?.();
          } else {
            toast.warning(res?.message || 'Update failed');
          }
        },
        {
          // handleResponse only calls the success callback on a 2xx. A refused approve
          // (RBE-01 BF-01: the stored result is Fail) is a 409 STORED_RESULT_FAIL — show the
          // server's own message as a warning, not a generic conflict error.
          silent: true,
          onError: ({ status, data }) => {
            if (status === 401) return; // session expiry is handled by handleResponse
            if (status === 409 && data?.code === 'STORED_RESULT_FAIL') {
              toast.warning(data?.message || 'This sample cannot be approved: its recorded result is Fail.');
              get_data();
              return;
            }
            toast.error(data?.message || data?.detail || 'Update failed');
          },
        }
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handle_reject = async () => {
    if (!reject_comment.trim()) return;
    set_rejecting(true);
    await call_status_api({
      record: active_record,
      sample_status: false,
      description: reject_comment,
      onSuccess: () => {
        set_qc_form_open(false);
        set_reject_mode(false);
        set_reject_comment('');
        get_data();
      },
    });
    set_rejecting(false);
  };

  // Called after form save succeeds — then approve via status API
  const handle_approve_after_save = async (record) => {
    await call_status_api({
      record,
      sample_status: true,
      onSuccess: () => {
        set_qc_form_open(false);
        get_data();
      },
    });
  };

  const submit_and_approve = async (values, { setSubmitting }) => {
    const data_flow = paramter_details.map((param, i) => ({
      sampling_config_id: param.sampling_config_id,
      value: param.value !== '' ? String(param.value) : String(values.param_values[i] || ''),
      remarks: '',
    }));
    set_save_error(null);
    try {
      await bp_save_sample_actual_data(
        {
          sample_detail_id: active_record?.sample_detail_id,
          data_flow,
          observation: values.observation || '',
          sample_number: values.sample_number || undefined,
          quantity: values.quantity !== null ? values.quantity : undefined,
          evidence_files: values.evidence_files.map((f) => f.originFileObj).filter(Boolean),
          evidence_description: values.evidence_description || undefined,
        },
        async (res) => {
          if (res?.status === 'failed') {
            toast.warning(res?.message);
            set_save_error(res?.message || 'The result could not be saved. Please review and try again.');
          } else {
            set_save_error(null);
            // The server judged the result (RBE-01 BF-01). An out-of-spec result is never
            // approved from here: the sample is closed on the server's Fail and goes to
            // investigation through the deviation it already raised.
            if (res?.is_oos) {
              toast.warning(res?.message || 'Out of specification — sample recorded as Fail');
              await call_status_api({
                record: active_record,
                sample_status: false,
                description: res?.message || 'Out of specification',
                onSuccess: () => {
                  set_qc_form_open(false);
                  get_data();
                },
              });
              return;
            }
            toast.success(res?.message || 'Sample saved');
            if (isESign) {
              setPendingApprovalSample(active_record);
              setPendingFormValues(values);
              setShowESignatureModal(true);
            } else {
              await handle_approve_after_save(active_record);
            }
          }
        }
      );
    } catch (err) {
      console.error(err);
      set_save_error(err?.response?.data?.message || err?.message || 'The result could not be saved. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const modifyConfirm = (record) => {
    navigate('/edit-batch', {
      state: {
        batch_number: record?.batch_number,
        recipe: record?.recipe_id,
        recipeName: record?.recipe_name,
      },
    });
    window?.localStorage?.removeItem('editRecipe', '');
  };

  // ── Columns ────────────────────────────────────────────────────────────────

  const allColumns = [
    {
      title: 'Date Received',
      dataIndex: 'date_time',
      key: 'date_time',
      defaultVisible: true,
      sortType: 'date',
      width: 150,
      render: (val) => (val ? <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(val)}</span> : '-'),
    },
    {
      title: 'Sample Number',
      dataIndex: 'sample_number',
      key: 'sample_number',
      defaultVisible: true,
      sortType: 'string',
      width: 170,
      render: (val) => (val ? <Clip text={val} max={160} /> : '-'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Sample Number"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.sample_number?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Batch Number',
      dataIndex: 'batch_number',
      key: 'batch_number',
      defaultVisible: true,
      sortType: 'string',
      width: 200,
      // the batch with its record under it (the separate record column is off by default)
      render: (val, r) => (
        <div style={{ lineHeight: 1.25, minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>{val || '-'}</div>
          {r.recipe_name ? <Clip text={r.recipe_name} max={190} small secondary /> : null}
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Batch Number"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.batch_number?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: mbmrAlias,
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      defaultVisible: false,
      sortType: 'string',
      width: 146,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${mbmrAlias}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.recipe_name?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Process',
      dataIndex: 'process_name',
      key: 'process_name',
      defaultVisible: true,
      sortType: 'string',
      width: 122,
      render: (val) => val || '-',
    },
    {
      title: 'Testing Type',
      dataIndex: 'testing_type',
      key: 'testing_type',
      defaultVisible: true,
      sortType: 'string',
      width: 220,
      // `testing_type` is the name; `testing_type_name` is the same value under the
      // canonical key. Older payloads only had the id — never render a bare number.
      render: (val, record) => {
        const name = val || record?.testing_type_name || '';
        if (!name) return <Text type="secondary">—</Text>;
        return (
          <div className="leading-tight" style={{ minWidth: 0 }}>
            <div>{name}</div>
            {record?.specification_text ? (
              <Clip text={record.specification_text} max={200} small secondary />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Result',
      dataIndex: 'sampling_result',
      key: 'sampling_result',
      defaultVisible: true,
      align: 'center',
      width: 86,
      render: (val, record) => {
        if (!val) return <Tooltip title="Not tested yet"><Text type="secondary">—</Text></Tooltip>;
        const failed = String(val).toLowerCase().startsWith('fail');
        return <IconLabel Icon={failed ? StFail : StPass} color={failed ? TONE.red : TONE.green} label={val} title={record?.observation || undefined} />;
      },
    },
    {
      title: 'Sampled By',
      dataIndex: 'employee_name',
      key: 'employee_name',
      defaultVisible: true,
      sortType: 'string',
      width: 106,
      render: (val) => val || '-',
    },
    {
      title: 'Machine Used',
      dataIndex: 'machine_name',
      key: 'machine_name',
      defaultVisible: true,
      sortType: 'string',
      width: 150,
      render: (val) => (val ? <Clip text={val} max={140} /> : '-'),
    },
    {
      // Every row in this queue is a QC sample by definition, so a QC/Inline flag told the analyst
      // nothing. The test category says which lab bench the sample goes to.
      title: 'Test Category',
      dataIndex: 'test_category',
      key: 'test_category',
      defaultVisible: true,
      sortType: 'string',
      align: 'center',
      width: 96,
      render: (val) => (val ? String(val).charAt(0) + String(val).slice(1).toLowerCase() : <Text type="secondary">—</Text>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      defaultVisible: true,
      sortType: 'string',
      align: 'center',
      width: 94,
      filters: ['Sent to QC', 'Under test', 'Completed', 'Approved', 'Rejected', 'Pending'].map((v) => ({ text: SAMPLE_STATUS[v].label + (v === 'Approved' || v === 'Completed' ? ` (${v})` : ''), value: v })),
      onFilter: (value, record) => record.status === value,
      render: (data) => <StatusTag value={data} />,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      defaultVisible: true,
      align: 'center',
      width: 64,
      render: (_, record) => {
        // "Approved" is the status the QC approval path writes; it is the same
        // terminal, passed state as the legacy "Completed" and must offer the
        // same "View Results" affordance (it used to fall through to the
        // "Enter QC Results" button, which is why the drawer never opened).
        if (record?.status === 'Completed' || record?.status === 'Approved') {
          return (
            <div className="flex items-center justify-center gap-2">
              <Tooltip title="Passed">
                <CheckCheck className="w-5 h-5" style={{ color: defaultColors.success }} />
              </Tooltip>
              <Tooltip title="View Results">
                <Button
                  type="text"
                  icon={<Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
                  onClick={() => open_view_modal(record)}
                />
              </Tooltip>
            </div>
          );
        }
        if (record?.status === 'Rejected') {
          return (
            <div className="flex items-center justify-center gap-2">
              <Tooltip title="View Comment">
                <Button
                  type="text"
                  icon={
                    <MessageCircle className="w-5 h-5" style={{ color: defaultColors.secondary }} />
                  }
                  onClick={() =>
                    Modal.info({
                      title: 'Rejection Comment',
                      content: record?.comment || 'No comment provided',
                    })
                  }
                />
              </Tooltip>
              <Tooltip title="Modify Batch">
                <Button
                  type="text"
                  icon={<Calendar className="w-5 h-5" style={{ color: defaultColors.warning }} />}
                  onClick={() => modifyConfirm(record)}
                />
              </Tooltip>
            </div>
          );
        }
        if (record?.status === 'Resampling') {
          return (
            <Tooltip title="Resampling">
              <RotateCw className="w-5 h-5" style={{ color: defaultColors.success }} />
            </Tooltip>
          );
        }
        // Sent to QC / Pending — open QC form
        return (
          <Tooltip title="Enter QC Results">
            <Button
              type="text"
              aria-label="Enter QC Results"
              icon={<StEnter className="w-5 h-5" style={{ color: defaultColors.primary }} />}
              onClick={() => open_qc_form(record)}
            />
          </Tooltip>
        );
      },
    },
  ];

  // "All samples" is a history view: never offer QC data entry there, but always
  // offer the detail drawer — before this the whole action column was dropped, so
  // there was no way to open a sample from that tab at all.
  const allTabColumns = useMemo(
    () =>
      allColumns.map((c) =>
        c.key !== 'action'
          ? c
          : {
              ...c,
              title: 'Details',
              render: (_, record) => (
                <Tooltip title={record?.sampling_result ? 'View results' : 'View sample'}>
                  <Button
                    type="text"
                    icon={<Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
                    onClick={() => open_view_modal(record)}
                  />
                </Tooltip>
              ),
            }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mbmrAlias, isDark]
  );

  // One strip, but it must count the tab you are looking at. `server_summary` only ever describes the
  // "All samples" set (bp_get_sample_detailss is the only one of the three endpoints that returns a
  // `summary`); using it on the other two tabs is what put 157 totals over a 5-row queue.
  const summaryData = useMemo(() => {
    const bucket = (s) => statusMeta(s?.status).bucket;
    const count = (rows, totalOverride) => ({
      total: totalOverride ?? rows?.length ?? 0,
      passed: rows?.filter((s) => bucket(s) === 'passed')?.length || 0,
      failed: rows?.filter((s) => bucket(s) === 'failed')?.length || 0,
      pending: rows?.filter((s) => bucket(s) === 'pending')?.length || 0,
      awaiting_result: rows?.filter((s) => s?.has_result === false)?.length || 0,
    });
    let src;
    let scope;
    if (tab === 'kind') {
      const t = kind_summary?.totals;
      src = t
        ? { total: t.total, passed: t.passed, failed: t.failed, pending: t.open, awaiting_result: t.open }
        : count(kind_rows);
      scope = batchFilter ? `drawn samples on ${batchFilter}` : 'drawn samples on this plant';
    } else if (tab === 'all') {
      // Server aggregate over the whole filtered set — the grid only holds one page of it.
      src = server_summary || count(all_data, all_total || undefined);
      scope = batchFilter ? `QC test entries on ${batchFilter}` : 'all QC test entries';
    } else {
      // The QC queue is the small "awaiting me" set; count exactly what is in it.
      src = count(sample_data, total || undefined);
      scope = batchFilter ? `QC queue for ${batchFilter}` : 'QC queue';
    }
    return {
      total_count: src.total || 0,
      completed_count: src.passed || 0,
      rejected_count: src.failed || 0,
      pending_count: src.pending || 0,
      awaiting_count: src.awaiting_result || 0,
      scope,
    };
  }, [sample_data, total, all_data, all_total, kind_rows, kind_summary, tab, server_summary, batchFilter]);

  // ── "Samples by kind" (SampleRecord) ───────────────────────────────────────
  // The breakdown itself: one row per sampling kind actually drawn, with the SK1 profile behind it.
  // `turnaround_hours` was defined per kind (IPC lab 8 h, bulk release 336 h, finished pack 120 h)
  // and used nowhere — it is the only thing on this page that can say what is LATE.
  const kindSummaryColumns = [
    { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 220, sortType: 'string',
      render: (v, r) => (
        <div>
          <Tag color={KIND_COLOR[v] || 'default'} style={{ margin: 0 }}>{v}</Tag>
          <div><Text type="secondary" style={{ fontSize: 11 }}>{r.label}{r.aliases?.length ? ` · stored also as ${r.aliases.join(', ')}` : ''}</Text></div>
        </div>
      ) },
    { title: 'Drawn', dataIndex: 'total', key: 'total', width: 90, align: 'right', sortType: 'number', render: (v) => <b style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</b> },
    { title: 'Awaiting result', dataIndex: 'open', key: 'open', width: 130, align: 'right', sortType: 'number',
      render: (v, r) => (v ? <CountChip Icon={StWait} color={TONE.amber} n={v} title={`${v} awaiting result${r.oldest_open_hours != null ? ` · oldest ${r.oldest_open_hours} h` : ''}`} /> : <Text type="secondary">0</Text>) },
    { title: 'Target (TAT)', dataIndex: 'turnaround_hours', key: 'turnaround_hours', width: 120, align: 'right', sortType: 'number',
      render: (v) => (v == null ? <Tooltip title="No turnaround target for this kind — it is stored, not tested"><Text type="secondary">no target</Text></Tooltip> : <span>{v} h</span>) },
    { title: 'Late', dataIndex: 'late', key: 'late', width: 90, align: 'right', sortType: 'number',
      render: (v, r) => (v ? <CountChip Icon={KpiWaitIcon} color={TONE.red} n={v} title={`${v} late: no result ${r.turnaround_hours} h after drawing`} /> : <Text type="secondary">0</Text>) },
    { title: 'Passed', dataIndex: 'passed', key: 'passed', width: 90, align: 'right', sortType: 'number', render: (v) => (v ? <CountChip Icon={StPass} color={TONE.green} n={v} title={`${v} passed`} /> : <Text type="secondary">0</Text>) },
    { title: 'Failed / OOS', dataIndex: 'failed', key: 'failed', width: 120, align: 'right', sortType: 'number', render: (v) => (v ? <CountChip Icon={StFail} color={TONE.red} n={v} title={`${v} failed / OOS`} /> : <Text type="secondary">0</Text>) },
    { title: 'Result from', dataIndex: 'result_by_role', key: 'result_by_role', width: 180, render: (v, r) => <span>{v || '—'}<div><Text type="secondary" style={{ fontSize: 11 }}>{r.result_location}</Text></div></span> },
    { title: 'Gate held', dataIndex: 'gate', key: 'gate', width: 160, filterable: true,
      render: (v) => (v && v !== 'NONE' ? <IconLabel Icon={StLock} color={TONE.amber} label={v.replace(/_/g, ' ').toLowerCase()} /> : <Text type="secondary">none</Text>) },
  ];

  // The per-batch sample list under the breakdown (only served when a batch number is given).
  const kindColumns = [
    { title: 'Sample no', dataIndex: 'sample_no', key: 'sample_no', width: 200, sortType: 'string', render: (v) => <b>{v}</b> },
    { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 170, sortType: 'string', filterable: true,
      render: (v, r) => <Tooltip title={r.kind_label}><Tag color={KIND_COLOR[v] || 'default'}>{v || '—'}</Tag></Tooltip> },
    { title: 'Stage', dataIndex: 'stage_code', key: 'stage_code', width: 80, render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, filterable: true, render: (v) => <StatusTag value={v} /> },
    { title: 'Result', key: 'result', width: 140, render: (_, r) => (r.result_value !== null && r.result_value !== undefined ? <b>{r.result_value}</b> : <Text type="secondary">{r.result_text || '—'}</Text>) },
    { title: 'Drawn', dataIndex: 'drawn_at', key: 'drawn_at', width: 160, sortType: 'date', render: (v) => (v ? formatDateTime(v) : '—') },
    { title: 'Age vs target', key: 'age', width: 140, sortType: 'number', dataIndex: 'age_hours',
      render: (v, r) => (v == null ? <Text type="secondary">—</Text> : (
        <AgeLabel late={!!r.late} days={null} text={`${v} h${r.turnaround_hours == null ? '' : ` / ${r.turnaround_hours} h`}`}
          title={r.late ? 'Past its turnaround target' : 'Hours since drawn / turnaround target'} />
      )) },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 160, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Re-draw', key: 'lineage', width: 110, render: (_, r) => (r.resample_no || r.retest_no ? <Tag>{r.resample_no ? `R${r.resample_no}` : ''}{r.retest_no ? `T${r.retest_no}` : ''}</Tag> : <Text type="secondary">—</Text>) },
  ];
  const kindRowsSorted = useMemo(() => [...kind_rows].sort((a, b) => String(a.kind || '').localeCompare(String(b.kind || '')) || String(a.sample_no || '').localeCompare(String(b.sample_no || ''))), [kind_rows]);

  const param_columns_view = [
    { title: 'Parameter', dataIndex: 'parameter_name', key: 'parameter_name', width: 160 },
    {
      title: 'Standard',
      dataIndex: 'standard',
      key: 'standard',
      width: 100,
      render: (v) => v || '-',
    },
    {
      title: 'Expected Range',
      key: 'expected_range',
      align: 'center',
      width: 140,
      render: (_, r) =>
        `${r?.sampling_min_value ?? '-'} – ${r?.sampling_max_value ?? '-'}${
          r?.unit_of_measure ? ` ${r.unit_of_measure}` : ''
        }`,
    },
    {
      title: 'Value',
      key: 'value',
      align: 'center',
      width: 120,
      render: (_, r) => {
        if (r.value === '' || r.value === null) return <Text type="secondary">—</Text>;
        const inRange =
          Number(r.value) >= Number(r.sampling_min_value) &&
          Number(r.value) <= Number(r.sampling_max_value);
        return <Tag color={inRange ? 'success' : 'error'}>{r.value}</Tag>;
      },
    },
  ];

  const doc_list = (docs) =>
    docs?.length > 0 ? (
      <>
        <Divider orientation="left" className="mt-3 mb-2">
          <Text className="text-xs text-gray-500">Uploaded Documents</Text>
        </Divider>
        <div className="space-y-2">
          {docs.map((doc) => (
            <div
              key={doc.document_id}
              className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 border border-gray-200"
            >
              <div className="flex items-center gap-2">
                <FileText size={15} className="text-blue-500" />
                <div>
                  <Link
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm"
                  >
                    {doc.file_name}
                  </Link>
                  {doc.description && (
                    <p className="text-xs text-gray-500 m-0">{doc.description}</p>
                  )}
                </div>
              </div>
              <Text type="secondary" className="text-xs shrink-0 ml-4">
                {doc.uploaded_by} · {formatDate(doc.created_dt)}
              </Text>
            </div>
          ))}
        </div>
      </>
    ) : null;

  useEffect(() => {
    get_data();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page_number, searchvalue, batchFilter]);
  useEffect(() => { get_all_samples(); }, [get_all_samples]);
  useEffect(() => { get_kind_samples(); }, [get_kind_samples]);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (batchFilter) next.set('batch_number', batchFilter); else next.delete('batch_number');
    next.delete('batch');
    if (tab && tab !== 'queue') next.set('tab', tab); else next.delete('tab');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchFilter, tab]);

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      {/* One white bordered page card with the shared page header as its title; the header stays put and only the body scrolls. */}
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          height: 'calc(100dvh - 120px)',
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.FlaskConical size={22} />}
            title={t('Sample Testing')}
            description="QC test entries and the samples drawn against batches"
            filters={
              <Input
                allowClear
                placeholder="Batch number"
                style={{ width: 200 }}
                value={batchFilter}
                onChange={(e) => { setBatchFilter(e.target.value.trim()); set_page_number(1); set_all_page(1); }}
              />
            }
          />
        }
      >
        {/* The counts as the app's KPI cards (the same card as the other pages). They change with the tab, so the line under them says
            what they count — reading a 157-row total over a five-row queue is how the strip lied before. */}
        <SummaryTiles
          testId="sample-tiles"
          minWidth={170}
          tiles={[
            { key: 'total', label: 'Total samples', value: summaryData.total_count, color: TONE.blue, Icon: KpiAllIcon, sub: summaryData.scope },
            { key: 'awaiting', label: 'Awaiting result', value: summaryData.awaiting_count, color: TONE.amber, Icon: KpiWaitIcon },
            { key: 'pending', label: 'In QC / pending', value: summaryData.pending_count, color: TONE.purple, Icon: KpiQcIcon },
            { key: 'passed', label: 'Passed / approved', value: summaryData.completed_count, color: TONE.green, Icon: KpiOkIcon },
            { key: 'failed', label: 'Failed / OOS', value: summaryData.rejected_count, color: TONE.red, Icon: KpiFailIcon },
          ]}
        />
        <Tabs activeKey={tab} onChange={setTab} size="large" tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }} items={[
          { key: 'queue', label: 'QC queue', children: (
            <>
              {/* the shared table in server mode: the API sends 10 samples a page and the total; search is the header's, sent to the server */}
              <DataTable
                tableKey="sampleTestingTable"
                serverSide
                searchable
                searchValue={searchvalue}
                onSearch={(v) => setSearchvalue(v)}
                searchPlaceholder="Search here..."
                columns={allColumns}
                data={sample_data}
                rowKey="sample_detail_id"
                height={null}
                minHeight={320}
                showCount={false}
                pagination={{ current: page_number, pageSize: 10, total, showSizeChanger: false, showTotal: (n) => `Total ${n} samples`, onChange: (value) => set_page_number(value) }}
                emptyMessage={batchFilter ? `No QC samples for ${batchFilter}` : 'No samples found'}
              />
            </>
          ) },
          { key: 'all', label: 'All samples', children: (
            <>
              <DataTable
                tableKey="sampleTestingAllTable"
                serverSide
                searchable
                searchValue={searchvalue}
                onSearch={(v) => setSearchvalue(v)}
                searchPlaceholder="Search here..."
                columns={allTabColumns}
                data={all_data}
                rowKey="sample_detail_id"
                height={null}
                minHeight={320}
                showCount={false}
                pagination={{ current: all_page, pageSize: 10, total: all_total, showSizeChanger: false, showTotal: (n) => `Total ${n} samples`, onChange: (value) => set_all_page(value) }}
                emptyMessage={batchFilter ? `No samples for ${batchFilter}` : 'No samples found'}
              />
            </>
          ) },
          { key: 'kind', label: `Samples by kind${kind_summary?.totals?.kinds ? ` (${kind_summary.totals.kinds})` : ''}`, children: (
            <>
              {/* QC queue / All samples list QC test entries (Sample_Details); this tab lists physical draws (SampleRecord). */}
              <div style={{ marginBottom: 8, fontSize: 12, color: isDark ? '#94a3b8' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Physical draws
                <Tooltip title="QC queue and All samples list the QC test entries in the batch record (…-QC-01). This tab lists the physical draws with custody, re-sample lineage and the release gate they hold (…-BIO-S01). Counts differ between the two.">
                  <StInfo size={13} aria-label="About this tab" style={{ cursor: 'help' }} />
                </Tooltip>
              </div>
              {kind_error ? <Alert type="error" showIcon style={{ marginBottom: 10 }} message="Could not load the sample kinds" description={kind_error} action={<Button size="small" onClick={get_kind_samples}>Retry</Button>} /> : null}
              {kind_summary?.totals?.late ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={`${kind_summary.totals.late} sample(s) past their turnaround target`}
                />
              ) : null}
              <DataTable
                tableKey="sampleTestingKindSummary"
                title={`${kind_summary?.totals?.kinds || 0} kind${kind_summary?.totals?.kinds === 1 ? '' : 's'} · ${kind_summary?.totals?.total || 0} drawn ${batchFilter ? `on ${batchFilter}` : 'on this plant'}`}
                bordered
                columnConfig={false}
                columns={kindSummaryColumns}
                data={kind_summary?.kinds || []}
                rowKey="kind"
                loading={kind_loading}
                height={null}
                pagination={false}
                exportable exportFilename={`sample-kinds-${batchFilter || 'plant'}`}
                onRefresh={get_kind_samples}
                locale={{
                  emptyText: (
                    <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {batchFilter ? `No samples were drawn on ${batchFilter}` : 'No samples drawn on this plant'}
                      </div>
                      <div style={{ opacity: 0.7, marginBottom: 12 }}>
                        {batchFilter
                          ? 'This batch has no SampleRecord rows. Its QC test entries, if any, are on the “All samples” tab.'
                          : 'Nothing has been drawn against a batch of the plant selected in the header.'}
                      </div>
                      {batchFilter ? <Button size="small" type="primary" onClick={() => setBatchFilter('')}>Clear the batch filter</Button> : null}
                    </div>
                  ),
                }}
              />
              <div style={{ marginTop: 16 }}>
                <DataTable
                  tableKey="sampleTestingKindTable"
                  title={batchFilter ? `Samples drawn on ${batchFilter} (${kindRowsSorted.length})` : 'Individual samples'}
                  bordered
                  columns={kindColumns}
                  data={kindRowsSorted}
                  rowKey="sample_no"
                  loading={kind_loading}
                  height={null}
                  searchable searchKeys={['sample_no', 'kind', 'status', 'location']}
                  pagination={{ pageSize: 20 }}
                  exportable exportFilename={`samples-by-kind-${batchFilter || 'batch'}`}
                  onRefresh={get_kind_samples}
                  onRow={(r) => ({
                    style: {
                      background: r.sample_no === highlightSample ? 'rgba(37,99,235,.12)' : r.status === 'FAIL' ? 'rgba(220,38,38,.08)' : undefined,
                      fontWeight: r.sample_no === highlightSample ? 600 : undefined,
                    },
                  })}
                  locale={{
                    emptyText: (
                      <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>The sample list is per batch</div>
                        <div style={{ opacity: 0.7 }}>
                          {batchFilter
                            ? `No individual samples for ${batchFilter}.`
                            : 'The breakdown above covers the whole plant. Type a batch number in the header to list its individual samples with their age against the turnaround target.'}
                        </div>
                      </div>
                    ),
                  }}
                />
              </div>
            </>
          ) },
        ]} />
      </Card>

      {/* ── QC Entry Form Modal ── */}
      <Formik
        enableReinitialize
        initialValues={{
          sample_number:
            active_record?.sample_number ||
            (active_record?.batch_number ? `${active_record.batch_number}-QC` : ''),
          quantity: null,
          observation: '',
          evidence_description: '',
          evidence_files: [],
          param_values: paramter_details.reduce((acc, _, i) => ({ ...acc, [i]: '' }), {}),
        }}
        validationSchema={Yup.object().shape({
          sample_number: Yup.string().required('Sample number is required'),
          quantity: (() => {
            let schema = Yup.number().nullable().required('Quantity is required');
            if (active_record?.sampling_min_value != null)
              schema = schema.min(
                active_record.sampling_min_value,
                `Quantity must be ≥ ${active_record.sampling_min_value}`
              );
            if (active_record?.sampling_max_value != null)
              schema = schema.max(
                active_record.sampling_max_value,
                `Quantity must be ≤ ${active_record.sampling_max_value}`
              );
            return schema;
          })(),
          observation: Yup.string().required('Observation is required'),
          param_values: Yup.lazy((obj) =>
            Yup.object(
              Object.keys(obj || {}).reduce((acc, key) => {
                const param = paramter_details[Number(key)];
                if (param && param.value === '') {
                  acc[key] = Yup.string().required('Value is required');
                }
                return acc;
              }, {})
            )
          ),
        })}
        onSubmit={submit_and_approve}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting, handleBlur }) => (
          <Modal
            open={qc_form_open}
            title={
              <div className="flex items-center gap-2">
                <FlaskConical size={18} className="text-blue-600" />
                <span>QC Results — {active_record?.batch_number}</span>
                {active_record?.status && (
                  <Tag color="processing" className="ml-1">
                    {active_record.status}
                  </Tag>
                )}
              </div>
            }
            width={1020}
            centered
            className="qc-results-modal"
            onCancel={() => {
              set_qc_form_open(false);
              set_reject_mode(false);
              set_reject_comment('');
            }}
            destroyOnHidden
            styles={{ body: { maxHeight: 640, overflowY: 'auto', overflowX: 'hidden' } }}
            footer={
              <div className="flex items-start gap-2">
                {reject_mode ? (
                  <div className="flex-1 flex flex-col gap-2">
                    <TextArea
                      rows={2}
                      placeholder="Enter rejection reason (required)"
                      value={reject_comment}
                      onChange={(e) => set_reject_comment(e.target.value)}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => {
                          set_reject_mode(false);
                          set_reject_comment('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        danger
                        loading={rejecting}
                        disabled={!reject_comment.trim()}
                        icon={<XCircle size={15} />}
                        onClick={handle_reject}
                      >
                        Confirm Reject
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        set_qc_form_open(false);
                        set_reject_comment('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      danger
                      icon={<XCircle size={15} />}
                      onClick={() => set_reject_mode(true)}
                    >
                      Reject
                    </Button>
                    {(() => {
                      // If any entered value is out of spec, this is a plain "Save Result"
                      // (an OOS value is recorded, not approved); in-spec keeps "Approve & Save".
                      const anyOOS = paramter_details.some((param, i) => {
                        const raw = param.value !== '' ? param.value : values.param_values[i];
                        const { hasSignal, inSpec } = specSignal(param, raw);
                        return hasSignal && !inSpec;
                      });
                      return (
                        <Button
                          type="primary"
                          loading={isSubmitting}
                          style={{
                            backgroundColor: anyOOS ? defaultColors.error : defaultColors.primary,
                            borderColor: anyOOS ? defaultColors.error : defaultColors.primary,
                          }}
                          onClick={handleSubmit}
                        >
                          {anyOOS ? 'Save Result' : 'Approve & Save'}
                        </Button>
                      );
                    })()}
                  </>
                )}
              </div>
            }
          >
            {/* Thin scrollbar on the scrollable body — same treatment as TableContainer. */}
            <style>{`
              .qc-results-modal .ant-modal-body {
                scrollbar-width: thin;
                scrollbar-color: ${isDark ? '#64748b' : '#94a3b8'} transparent;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar {
                width: 6px;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-track {
                background: transparent;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-thumb {
                background: ${isDark ? '#64748b' : '#94a3b8'};
                border-radius: 4px;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
                background: ${isDark ? '#94a3b8' : '#64748b'};
              }
            `}</style>
            {/* Info strip — same identity fields as the read-only View-Results modal
                (Operation, Stage, ledger Sample Number) so the analyst knows exactly
                which sample they are entering results for. */}
            <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
              <div>
                <Text type="secondary" className="text-xs block">
                  {mbmrAlias}
                </Text>
                <Text strong>{active_record?.recipe_name || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Process
                </Text>
                <Text strong>{active_record?.process_name || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Testing Type
                </Text>
                <Text strong>{active_record?.testing_type || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Operation
                </Text>
                <Text strong>{active_record?.operation_name || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Stage
                </Text>
                <Text strong>{active_record?.operation_stage || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Sample Number
                </Text>
                <Text strong>{active_record?.sample_number || '-'}</Text>
              </div>
            </div>

            {/* Failed-save banner — the save error stays visible in the modal, not only as a toast. */}
            {save_error && (
              <Alert
                type="error"
                showIcon
                className="mb-4"
                message="Result not saved"
                description={save_error}
                closable
                onClose={() => set_save_error(null)}
              />
            )}

            {/* QC Quantity Range */}
            {(active_record?.sampling_min_value != null ||
              active_record?.sampling_max_value != null) && (
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <Text className="text-xs text-yellow-700 font-medium block mb-2">
                  QC Quantity Range
                </Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary" className="text-xs block">
                      Min Quantity
                    </Text>
                    <Text strong>{active_record?.sampling_min_value ?? '-'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" className="text-xs block">
                      Max Quantity
                    </Text>
                    <Text strong>{active_record?.sampling_max_value ?? '-'}</Text>
                  </Col>
                </Row>
              </div>
            )}

            {/* Conditional Parameter */}
            {active_record?.conditional_parameter && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Text className="text-xs text-blue-600 font-medium block mb-2">
                  Conditional Parameter
                </Text>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Parameter
                    </Text>
                    <Text strong>{active_record.conditional_parameter.parameter_name}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Min Value
                    </Text>
                    <Text strong>
                      {active_record.conditional_parameter.min_value ?? '-'}
                      {active_record.conditional_parameter.unit && (
                        <span className="ml-1 text-gray-500 text-xs">
                          {active_record.conditional_parameter.unit}
                        </span>
                      )}
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Max Value
                    </Text>
                    <Text strong>
                      {active_record.conditional_parameter.max_value ?? '-'}
                      {active_record.conditional_parameter.unit && (
                        <span className="ml-1 text-gray-500 text-xs">
                          {active_record.conditional_parameter.unit}
                        </span>
                      )}
                    </Text>
                  </Col>
                </Row>
              </div>
            )}

            <Form layout="vertical" onFinish={handleSubmit}>
              <Row gutter={16}>
                {/* Sample Number */}
                <Col span={12}>
                  <Form.Item
                    label="Sample Number"
                    required
                    validateStatus={touched.sample_number && errors.sample_number ? 'error' : ''}
                    help={touched.sample_number && errors.sample_number ? errors.sample_number : undefined}
                  >
                    <Input
                      placeholder="QC-assigned sample number"
                      value={values.sample_number}
                      onChange={(e) => setFieldValue('sample_number', e.target.value)}
                      onBlur={() => handleBlur({ target: { name: 'sample_number' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Quantity */}
                <Col span={12}>
                  <Form.Item
                    label="Quantity"
                    required
                    validateStatus={touched.quantity && errors.quantity ? 'error' : ''}
                    help={
                      touched.quantity && errors.quantity
                        ? errors.quantity
                        : active_record?.sampling_min_value != null ||
                          active_record?.sampling_max_value != null
                        ? `Range: ${active_record?.sampling_min_value ?? '-'} – ${
                            active_record?.sampling_max_value ?? '-'
                          }`
                        : undefined
                    }
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="Actual quantity"
                      min={active_record?.sampling_min_value ?? 0}
                      max={active_record?.sampling_max_value ?? undefined}
                      value={values.quantity}
                      onChange={(val) => setFieldValue('quantity', val)}
                      onBlur={() => handleBlur({ target: { name: 'quantity' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Dynamic parameter inputs */}
                {paramter_details.map((param, index) => (
                  <Col span={12} key={param.sampling_config_id ?? index}>
                    <Form.Item
                      label={
                        <span>
                          {param.parameter_name}
                          {param.standard && (
                            <Tag color="default" className="ml-2 text-xs">
                              {param.standard}
                            </Tag>
                          )}
                          <span className="ml-2 text-gray-400 text-xs font-normal">
                            ({param.sampling_min_value ?? '-'} – {param.sampling_max_value ?? '-'})
                          </span>
                        </span>
                      }
                      validateStatus={
                        touched.param_values?.[index] && errors.param_values?.[index] ? 'error' : ''
                      }
                      help={touched.param_values?.[index] && errors.param_values?.[index]}
                    >
                      {param.value !== '' ? (
                        <Tag
                          color={
                            Number(param.value) >= Number(param.sampling_min_value) &&
                            Number(param.value) <= Number(param.sampling_max_value)
                              ? 'success'
                              : 'error'
                          }
                          className="text-sm px-3 py-1"
                        >
                          {param.value}
                        </Tag>
                      ) : (
                        (() => {
                          // Live in/out-of-spec signal as the analyst types — same min/max
                          // comparison the saved-value Tag uses, applied to the live value.
                          const { hasSignal, inSpec } = specSignal(param, values.param_values[index]);
                          return (
                            <>
                              <InputNumber
                                className="w-full"
                                placeholder="Enter value"
                                status={hasSignal && !inSpec ? 'error' : ''}
                                value={values.param_values[index]}
                                onChange={(val) =>
                                  setFieldValue('param_values', { ...values.param_values, [index]: val })
                                }
                              />
                              {hasSignal && (
                                <Tag
                                  color={inSpec ? 'success' : 'error'}
                                  className="mt-1 text-xs"
                                >
                                  {inSpec ? 'In spec' : 'Out of spec'}
                                </Tag>
                              )}
                            </>
                          );
                        })()
                      )}
                    </Form.Item>
                  </Col>
                ))}

                {/* Observation */}
                <Col span={24}>
                  <Form.Item
                    label="Observation"
                    required
                    validateStatus={touched.observation && errors.observation ? 'error' : ''}
                    help={touched.observation && errors.observation ? errors.observation : undefined}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder="Free-text observation"
                      value={values.observation}
                      onChange={(e) => setFieldValue('observation', e.target.value)}
                      onBlur={() => handleBlur({ target: { name: 'observation' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Evidence description */}
                <Col span={24}>
                  <Form.Item label="Evidence Description">
                    <Input
                      placeholder="Description for uploaded files"
                      value={values.evidence_description}
                      onChange={(e) => setFieldValue('evidence_description', e.target.value)}
                    />
                  </Form.Item>
                </Col>

                {/* Evidence upload */}
                <Col span={24}>
                  <Form.Item label="Evidence Files">
                    <Upload
                      multiple
                      beforeUpload={() => false}
                      fileList={values.evidence_files}
                      onChange={({ fileList }) => setFieldValue('evidence_files', fileList)}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    >
                      <Button icon={<UploadCloud size={15} />}>Attach Files</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* Existing documents */}
                {documents?.length > 0 && <Col span={24}>{doc_list(documents)}</Col>}
              </Row>
            </Form>
          </Modal>
        )}
      </Formik>

      {/* ── View Results Modal (Completed / Rejected) ── */}
      <Modal
        open={view_open}
        title={
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-blue-600" />
            <span>View Results — {view_record?.sample_number}</span>
            {view_record?.status && (
              <Tag color={statusMeta(view_record.status).color} className="ml-1">
                {statusMeta(view_record.status).label}
              </Tag>
            )}
          </div>
        }
        width={700}
        footer={null}
        onCancel={() => set_view_open(false)}
        destroyOnHidden
      >
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
          {[
            ['Batch', view_record?.batch_number],
            [mbmrAlias, view_record?.recipe_name],
            ['Testing Type', view_record?.testing_type || view_record?.testing_type_name],
            ['Operation', view_record?.operation_name],
            ['Stage', view_record?.operation_stage],
            ['Specification', view_record?.specification_text || view_record?.acceptance_criteria],
            ['Sampled by', view_record?.sampled_by || view_record?.employee_name],
            ['Tested by', view_record?.tested_by],
            [
              'Completed on',
              view_record?.completed_on ? formatDateTime(view_record.completed_on) : null,
            ],
          ].map(([label, value]) => (
            <div key={label}>
              <Text type="secondary" className="text-xs block">
                {label}
              </Text>
              <Text strong>{value || '-'}</Text>
            </div>
          ))}
        </div>

        {/* Result + observation — the reason the drawer exists. */}
        {view_record?.sampling_result ? (
          <div className="mb-4 p-3 rounded-lg border border-gray-200 text-sm">
            <Space align="center" className="mb-1">
              <Text type="secondary" className="text-xs">
                Result
              </Text>
              <Tag
                color={
                  String(view_record.sampling_result).toLowerCase().startsWith('fail')
                    ? 'error'
                    : 'success'
                }
              >
                {view_record.sampling_result}
              </Tag>
              {view_record?.sample_kind_label ? (
                <Tag color={KIND_COLOR[view_record.sample_kind] || 'default'}>
                  {view_record.sample_kind_label}
                </Tag>
              ) : null}
            </Space>
            {view_record?.observation ? (
              <div>
                <Text type="secondary" className="text-xs block">
                  Observation
                </Text>
                <Text>{view_record.observation}</Text>
              </div>
            ) : null}
            {view_record?.remarks ? (
              <div className="mt-1">
                <Text type="secondary" className="text-xs block">
                  Remarks
                </Text>
                <Text>{view_record.remarks}</Text>
              </div>
            ) : null}
          </div>
        ) : (
          !loading_view && (
            <div className="mb-4">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  view_empty_reason ||
                  `No QC result recorded yet — this sample is ${statusMeta(view_record?.status).label}.`
                }
              />
            </div>
          )
        )}

        <DataTable
          height={null}
          bordered
          pagination={false}
          loading={loading_view}
          rowKey={(r) => r.sampling_config_id ?? `tt-${r.testing_type_id}-${r.parameter_name}`}
          columns={param_columns_view}
          data={view_params}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No test parameters are configured for this testing type"
              />
            ),
          }}
        />

        {doc_list(view_docs)}
      </Modal>

      {/* ── E-Signature ── */}
      <DualESignatureAuth
        visible={showESignatureModal}
        onCancel={() => {
          setShowESignatureModal(false);
          setPendingApprovalSample(null);
          setPendingFormValues(null);
        }}
        onSubmit={async () => {
          await handle_approve_after_save(pendingApprovalSample);
          setShowESignatureModal(false);
          setPendingApprovalSample(null);
          setPendingFormValues(null);
        }}
        signatureType="sample_approval"
        action_type="sample_approval"
        signatureMeaning="Sample Approval"
        documentType="SAMPLE"
        sample_number={pendingApprovalSample?.sample_number || ''}
        documentId={pendingApprovalSample?.sample_detail_id || ''}
        documentNumber={pendingApprovalSample?.sample_number || ''}
        documentData={{
          sample_detail_id: pendingApprovalSample?.sample_detail_id || '',
          sample_number: pendingApprovalSample?.sample_number || '',
          batch_number: pendingApprovalSample?.batch_number || '',
          operation_id: pendingApprovalSample?.operation_id || '',
          batch_id: pendingApprovalSample?.batch_id || '',
          status: pendingApprovalSample?.status || '',
        }}
        config={{
          enableBasic: true,
          enableCFR: true,
          defaultMode: 'cfr',
          requireComment: true,
          title: 'E-Signature for Sample Approval',
        }}
      />
    </div>
  );
}

export default SampleTesting;
